import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { audit } from '../../services/apiClient';

const PAGE_SIZE_OPTIONS = new Set([20, 50, 100]);
const ENTITY_TYPE_OPTIONS = [
  { value: 'all', label: 'Todas entidades' },
  { value: 'store_review', label: 'Avaliacao de loja' },
  { value: 'store', label: 'Loja' },
  { value: 'courier', label: 'Motoboy' },
  { value: 'order', label: 'Pedido' },
  { value: 'system', label: 'Sistema' },
];
const ENTITY_TYPE_VALUES = new Set(ENTITY_TYPE_OPTIONS.map((option) => option.value));

function parsePositiveInt(value, fallbackValue) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return fallbackValue;
  return Math.trunc(parsed);
}

function normalizeDateInput(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return '';
}

function isInvalidDateRange(fromValue, toValue) {
  return Boolean(fromValue && toValue && fromValue > toValue);
}

function parseAuditQuery(search) {
  const params = new URLSearchParams(search || '');

  const rawEntity = String(params.get('au_entity') || 'all').trim().toLowerCase();
  const entityType = ENTITY_TYPE_VALUES.has(rawEntity) ? rawEntity : 'all';

  const actionPrefix = String(params.get('au_action') || '').trim();
  const searchText = String(params.get('au_q') || '').trim();
  const createdFrom = normalizeDateInput(params.get('au_from'));
  const createdTo = normalizeDateInput(params.get('au_to'));

  const page = parsePositiveInt(params.get('au_page'), 1);
  const parsedPageSize = parsePositiveInt(params.get('au_size'), 20);
  const pageSize = PAGE_SIZE_OPTIONS.has(parsedPageSize) ? parsedPageSize : 20;

  return {
    entityType,
    actionPrefix,
    search: searchText,
    createdFrom,
    createdTo,
    page,
    pageSize,
  };
}

function buildPaginationNumbers(currentPage, totalPages) {
  if (totalPages <= 0) return [];
  let start = Math.max(1, currentPage - 2);
  let end = Math.min(totalPages, start + 4);
  start = Math.max(1, end - 4);
  const pages = [];
  for (let value = start; value <= end; value += 1) {
    pages.push(value);
  }
  return pages;
}

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('pt-BR');
}

function formatAction(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'store_review_hidden') return 'Ocultar avaliacao';
  if (normalized === 'store_review_published') return 'Publicar avaliacao';
  if (normalized === 'store_reviews_batch_hidden') return 'Ocultar em lote';
  if (normalized === 'store_reviews_batch_published') return 'Publicar em lote';
  if (normalized === 'store_approved') return 'Aprovar loja';
  if (normalized === 'store_rejected') return 'Reprovar loja';
  if (normalized === 'courier_approved') return 'Aprovar motoboy';
  if (normalized === 'courier_rejected') return 'Reprovar motoboy';
  return String(value || '-');
}

function getDetailsReason(details) {
  if (!details || typeof details !== 'object') return '-';
  const reason = String(details.reason || '').trim();
  return reason || '-';
}

function getDetailsResult(details) {
  if (!details || typeof details !== 'object') return '-';
  const processedCount = Number(details.processed_count || 0);
  const updatedCount = Number(details.updated_count || 0);
  if (processedCount > 0 || updatedCount > 0) {
    return `${updatedCount}/${processedCount}`;
  }
  return String(details.target_status || '-');
}

function downloadBlob(fileName, blob) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function AdminAudit() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialQuery = parseAuditQuery(location.search);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(initialQuery.search);
  const [search, setSearch] = useState(initialQuery.search);
  const [actionPrefixInput, setActionPrefixInput] = useState(initialQuery.actionPrefix);
  const [actionPrefix, setActionPrefix] = useState(initialQuery.actionPrefix);
  const [createdFromInput, setCreatedFromInput] = useState(initialQuery.createdFrom);
  const [createdToInput, setCreatedToInput] = useState(initialQuery.createdTo);
  const [createdFrom, setCreatedFrom] = useState(initialQuery.createdFrom);
  const [createdTo, setCreatedTo] = useState(initialQuery.createdTo);
  const [entityType, setEntityType] = useState(initialQuery.entityType);
  const [page, setPage] = useState(initialQuery.page);
  const [pageSize, setPageSize] = useState(initialQuery.pageSize);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const invalidInputDateRange = isInvalidDateRange(createdFromInput, createdToInput);
  const invalidAppliedDateRange = isInvalidDateRange(createdFrom, createdTo);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError('');

      if (isInvalidDateRange(createdFrom, createdTo)) {
        if (mounted) {
          setData([]);
          setTotal(0);
          setPages(0);
          setError('Periodo invalido: data inicial nao pode ser maior que data final.');
          setLoading(false);
        }
        return;
      }

      try {
        const response = await audit.list({
          entityType: entityType !== 'all' ? entityType : undefined,
          actionPrefix: actionPrefix || undefined,
          q: search || undefined,
          createdFrom: createdFrom || undefined,
          createdTo: createdTo || undefined,
          page,
          pageSize,
        });
        if (!mounted) return;

        const items = Array.isArray(response)
          ? response
          : (Array.isArray(response?.items) ? response.items : []);
        const nextTotal = Array.isArray(response)
          ? items.length
          : Number(response?.total ?? items.length);
        const nextPages = Array.isArray(response)
          ? (items.length > 0 ? 1 : 0)
          : Number(response?.pages ?? 0);

        if (nextPages > 0 && page > nextPages) {
          setPage(nextPages);
          return;
        }
        if (nextPages === 0 && page !== 1) {
          setPage(1);
          return;
        }

        setData(items);
        setTotal(Number.isFinite(nextTotal) ? nextTotal : items.length);
        setPages(Number.isFinite(nextPages) ? nextPages : 0);
      } catch {
        if (mounted) setError('Erro ao carregar auditoria');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [entityType, actionPrefix, search, createdFrom, createdTo, page, pageSize]);

  useEffect(() => {
    const queryState = parseAuditQuery(location.search);
    setEntityType((prev) => (prev === queryState.entityType ? prev : queryState.entityType));
    setActionPrefix((prev) => (prev === queryState.actionPrefix ? prev : queryState.actionPrefix));
    setActionPrefixInput((prev) => (prev === queryState.actionPrefix ? prev : queryState.actionPrefix));
    setSearch((prev) => (prev === queryState.search ? prev : queryState.search));
    setSearchInput((prev) => (prev === queryState.search ? prev : queryState.search));
    setCreatedFrom((prev) => (prev === queryState.createdFrom ? prev : queryState.createdFrom));
    setCreatedFromInput((prev) => (prev === queryState.createdFrom ? prev : queryState.createdFrom));
    setCreatedTo((prev) => (prev === queryState.createdTo ? prev : queryState.createdTo));
    setCreatedToInput((prev) => (prev === queryState.createdTo ? prev : queryState.createdTo));
    setPage((prev) => (prev === queryState.page ? prev : queryState.page));
    setPageSize((prev) => (prev === queryState.pageSize ? prev : queryState.pageSize));
  }, [location.search]);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search || '');
    const nextParams = new URLSearchParams(location.search || '');
    const keys = ['au_entity', 'au_action', 'au_q', 'au_from', 'au_to', 'au_page', 'au_size'];
    keys.forEach((key) => nextParams.delete(key));

    if (entityType !== 'all') nextParams.set('au_entity', entityType);
    if (actionPrefix) nextParams.set('au_action', actionPrefix);
    if (search) nextParams.set('au_q', search);
    if (createdFrom) nextParams.set('au_from', createdFrom);
    if (createdTo) nextParams.set('au_to', createdTo);
    if (page > 1) nextParams.set('au_page', String(page));
    if (pageSize !== 20) nextParams.set('au_size', String(pageSize));

    const currentSearch = currentParams.toString();
    const nextSearch = nextParams.toString();
    if (currentSearch === nextSearch) return;

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    );
  }, [navigate, location.pathname, location.search, entityType, actionPrefix, search, createdFrom, createdTo, page, pageSize]);

  const applySearch = () => {
    setPage(1);
    setSearch(String(searchInput || '').trim());
  };

  const applyActionPrefix = () => {
    setPage(1);
    setActionPrefix(String(actionPrefixInput || '').trim());
  };

  const applyDateRange = () => {
    const nextFrom = normalizeDateInput(createdFromInput);
    const nextTo = normalizeDateInput(createdToInput);
    if (isInvalidDateRange(nextFrom, nextTo)) {
      setError('Periodo invalido: data inicial nao pode ser maior que data final.');
      return;
    }
    setError('');
    setPage(1);
    setCreatedFrom(nextFrom);
    setCreatedTo(nextTo);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setActionPrefixInput('');
    setActionPrefix('');
    setCreatedFromInput('');
    setCreatedFrom('');
    setCreatedToInput('');
    setCreatedTo('');
    setEntityType('all');
    setPage(1);
    setError('');
  };

  const handleExportCsv = async () => {
    if (isInvalidDateRange(createdFrom, createdTo)) {
      setError('Periodo invalido: data inicial nao pode ser maior que data final.');
      return;
    }

    setExportingCsv(true);
    setError('');
    try {
      const blob = await audit.exportCsv({
        entityType: entityType !== 'all' ? entityType : undefined,
        actionPrefix: actionPrefix || undefined,
        q: search || undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        limit: 50000,
      });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const fileName = `auditoria-${stamp}.csv`;
      downloadBlob(fileName, blob);
    } catch {
      setError('Erro ao exportar auditoria');
    } finally {
      setExportingCsv(false);
    }
  };

  const paginationNumbers = buildPaginationNumbers(page, pages);

  const columns = useMemo(() => [
    {
      key: 'created_at',
      header: 'Data',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'actor_email',
      header: 'Admin',
      render: (row) => row.actor_email || '-',
    },
    {
      key: 'action',
      header: 'Acao',
      render: (row) => formatAction(row.action),
    },
    {
      key: 'entity_type',
      header: 'Entidade',
      render: (row) => row.entity_type || '-',
    },
    {
      key: 'entity_id',
      header: 'ID',
      render: (row) => row.entity_id || '-',
    },
    {
      key: 'reason',
      header: 'Motivo',
      render: (row) => getDetailsReason(row.details),
    },
    {
      key: 'result',
      header: 'Resultado',
      render: (row) => getDetailsResult(row.details),
    },
    {
      key: 'details',
      header: 'Detalhes',
      render: (row) => (
        <button className="admin-button secondary" onClick={() => setSelectedLog(row)}>
          Ver detalhes
        </button>
      ),
    },
  ], []);

  return (
    <div className="admin-section">
      <PageHeader title="Auditoria" subtitle="Historico de acoes administrativas" />

      <div className="admin-card">
        <div className="admin-inline-actions" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="admin-inline-actions">
            <select
              className="admin-input"
              value={entityType}
              onChange={(event) => {
                setPage(1);
                setEntityType(event.target.value);
              }}
              style={{ minWidth: 200 }}
            >
              {ENTITY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <input
              className="admin-input"
              value={actionPrefixInput}
              onChange={(event) => setActionPrefixInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applyActionPrefix();
              }}
              placeholder="Prefixo da acao (ex: store_review)"
              style={{ minWidth: 230 }}
            />
            <button className="admin-button secondary" onClick={applyActionPrefix}>Aplicar acao</button>

            <input
              className="admin-input"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applySearch();
              }}
              placeholder="Buscar por admin, acao, entidade ou id..."
              style={{ minWidth: 280 }}
            />
            <button className="admin-button secondary" onClick={applySearch}>Buscar</button>

            <input
              type="date"
              className="admin-input"
              value={createdFromInput}
              onChange={(event) => setCreatedFromInput(normalizeDateInput(event.target.value))}
            />
            <input
              type="date"
              className="admin-input"
              value={createdToInput}
              onChange={(event) => setCreatedToInput(normalizeDateInput(event.target.value))}
            />
            <button
              className="admin-button secondary"
              onClick={applyDateRange}
              disabled={invalidInputDateRange}
            >
              Aplicar periodo
            </button>
            {invalidInputDateRange && (
              <span className="admin-inline-hint" style={{ color: '#ff9a9e' }}>
                Data inicial nao pode ser maior que a data final.
              </span>
            )}

            <button className="admin-button secondary" onClick={clearFilters}>Limpar</button>
            <button
              className="admin-button secondary"
              onClick={handleExportCsv}
              disabled={exportingCsv || invalidAppliedDateRange}
            >
              {exportingCsv ? 'Exportando...' : 'Exportar CSV'}
            </button>
          </div>

          <select
            className="admin-input"
            value={pageSize}
            onChange={(event) => {
              const parsedSize = parsePositiveInt(event.target.value, 20);
              const nextSize = PAGE_SIZE_OPTIONS.has(parsedSize) ? parsedSize : 20;
              setPage(1);
              setPageSize(nextSize);
            }}
            style={{ maxWidth: 140 }}
          >
            <option value={20}>20 / pagina</option>
            <option value={50}>50 / pagina</option>
            <option value={100}>100 / pagina</option>
          </select>
        </div>

        <DataTable
          columns={columns}
          data={data}
          loading={loading}
          error={error}
          emptyMessage="Nenhum log encontrado"
        />

        <div className="admin-inline-actions" style={{ justifyContent: 'space-between', marginTop: 10 }}>
          <span className="admin-inline-hint">
            Total: {total} | Pagina {pages === 0 ? 0 : page} de {pages}
          </span>
          <div className="admin-inline-actions">
            <button
              className="admin-button secondary"
              disabled={page <= 1}
              onClick={() => setPage(1)}
            >
              Primeira
            </button>
            <button
              className="admin-button secondary"
              disabled={page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Anterior
            </button>
            <div className="admin-pagination-pages">
              {paginationNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  className={`admin-button secondary admin-pagination-page ${pageNumber === page ? 'active' : ''}`}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
            </div>
            <button
              className="admin-button secondary"
              disabled={pages === 0 || page >= pages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Proxima
            </button>
            <button
              className="admin-button secondary"
              disabled={pages === 0 || page >= pages}
              onClick={() => setPage(pages)}
            >
              Ultima
            </button>
          </div>
        </div>
      </div>

      {selectedLog && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3 style={{ marginBottom: 8 }}>Detalhes da auditoria</h3>
            <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
              <span className="admin-inline-hint">Data: {formatDate(selectedLog.created_at)}</span>
              <span className="admin-inline-hint">Admin: {selectedLog.actor_email || '-'}</span>
              <span className="admin-inline-hint">Acao: {formatAction(selectedLog.action)}</span>
              <span className="admin-inline-hint">Entidade: {selectedLog.entity_type || '-'}</span>
              <span className="admin-inline-hint">ID: {selectedLog.entity_id || '-'}</span>
            </div>

            <pre
              style={{
                margin: 0,
                maxHeight: '44vh',
                overflow: 'auto',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
                padding: 12,
                background: 'rgba(255,255,255,0.02)',
                color: '#E4E4EE',
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(selectedLog.details || {}, null, 2)}
            </pre>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="admin-button secondary" onClick={() => setSelectedLog(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
